

// 1. BaseDrawable - supports nesting
import {generateUUID} from "three/src/math/MathUtils.js";
import {Container, Sprite} from "pixi.js";
import {Surface} from "../../../surface/primitives/surface";
import {Rect, RectConstructor} from "../../../primitives/rect";
import {DebugRectPixi} from "../drawables/debug-rect.pixi";
import {TouchPrimitive} from "../../../inputs/primitives/touch-primitive";
import {Vector2} from "three";

export class PixiObject {
    public sprite: Container;
    // public touchPrimitive: TouchPrimitive
    public touchPrimitive: TouchPrimitive;
    config: any;

    protected children: PixiObject[] = [];
    public _parent: PixiObject | Surface | null = null;
    public _surface: Surface | null = null;
    id: string = generateUUID();

    constructor(
        config?: & { id?: string, debug?: boolean }
    ) {
        let myId = generateUUID();
        this.config = config || {};
        if (config?.id) {
            myId = config.id + "-" + myId.slice(0, 5);
        }
        this.sprite = new Container();
        this.sprite.label = myId;
        this.id = myId;
        this.touchPrimitive = new TouchPrimitive({
            width: 0,
            height: 0,
            id: myId
        })
        this.touchPrimitive.intersectsPoint = this.intersectsPoint.bind(this);
        this.touchPrimitive.__translateParentCoordToLocalCoord = this.__translateParentCoordToLocalCoord.bind(this);
        if (config.debug ?? false) {
            this.setupBackgroundDebugRect();
        }
        this.setupDefaultInteractions();
    }

    protected setupDefaultInteractions() {
        // Override in subclasses
        this.touchPrimitive.onPressed((pt) => {
            this.sprite.alpha = 0.5;
            return false;
        });
        this.touchPrimitive.onHover((pt) => {
            this.sprite.alpha = 1;
            return false;
        });
    }

    public setRect(config: RectConstructor) {
        const r = new Rect(config)
        this.sprite.x =  r.x;
        this.sprite.y = r.y;
        this.sprite.width = r.width;
        this.sprite.height = r.height;
        this.updateTouchBounds()
    }

    set x(newX: number) {
        this.sprite.x = newX;
        this.updateTouchBounds()
    }

    set y(newY: number) {
        this.sprite.y = newY
        this.updateTouchBounds()
    }

    get x() {
        return this.sprite.x;
    }

    get y() {
        return this.sprite.y;
    }


    set width(newWidth: number) {
        this.sprite.width = newWidth;
        this.updateTouchBounds()
    }

    set height(newHeight: number) {
        this.sprite.height = newHeight
        this.updateTouchBounds()
    }

    get width() {
        return this.sprite.width;
    }

    get height() {
        return this.sprite.height;
    }


    /**
     * Add a child drawable to this drawable
     */
    public addChild(child: PixiObject) {
        this.sprite.addChild(child.sprite);
        this.updateTouchBounds()
        this.touchPrimitive.addTouchChild(child.touchPrimitive);
        this.children.push(child);
        child._parent = this;
        child._surface = this._surface;

        child.onAddedToParent(this);
        this.requestRender();
        return child;
    }

    /**
     * Remove a child drawable
     */
    public removeChild(child: PixiObject) {
        const index = this.children.indexOf(child);
        if (index === -1) return;

        this.children.splice(index, 1);
        this.sprite.removeChild(child.sprite);
        this.touchPrimitive.tp_children.delete(child.touchPrimitive.id);

        child._parent = null;
        child._surface = null;
        child.onRemovedFromParent();
        this.requestRender();
    }

    // /**
    //  * Add this drawable to a surface
    //  */
    // public addToSurface(surface: Surface) {
    //     this._surface = surface;
    //     this._parent = surface;
    //
    //     surface.stage.addChild(this.sprite);
    //     surface.getTouchPrimitive().addTouchChild(this.touchPrimitive);
    //
    //     // Propagate surface to all children
    //     this.propagateSurface(surface);
    //     this.onAddedToSurface(surface);
    // }

    public propagateSurface(surface: Surface) {
        this._surface = surface;
        this.children.forEach(child => child.propagateSurface(surface));
    }

    protected requestRender() {
        if (this._surface) {
            this._surface.markForRender();
        }
    }

    public updateTouchBounds() {
        this.touchPrimitive.x = this.x;
        this.touchPrimitive.y = this.y;
        this.touchPrimitive.width = this.width;
        this.touchPrimitive.height = this.height;
        for (let child of this.children) {
            child.updateTouchBounds();
        }
    }

    public onAddedToParent(parent: PixiObject) {
        // Override in subclasses
    }

    public onAddedToSurface(surface: Surface) {
        // Override in subclasses
    }

    protected onRemovedFromParent() {
        // Override in subclasses
    }

    public destroy() {
        // Destroy all children first
        [...this.children].forEach(child => child.destroy());

        // Remove from parent
        if (this._parent instanceof PixiObject) {
            this._parent.removeChild(this);
        } else if (this._parent instanceof Surface) {
            this._parent.stage.removeChild(this.sprite);
            this._parent.getTouchPrimitive().tp_children.delete(this.touchPrimitive.id);
        }

        this.sprite.destroy({ children: true });
    }

    setupBackgroundDebugRect() {
        const drp = new DebugRectPixi({
            left: 0,
            top: 0,
            right: this.width,
            bottom: this.height,
            roundness: 10,
            colour: [Math.random(), Math.random(), Math.random(), 0.5],
        });
        this.sprite.addChild(drp.sprite);
        drp.sprite.zIndex = 100;
    }

    intersectsPoint(point: Vector2): boolean {
        // const surfaceGlobalPos = surfaceParent.getGlobalPosition();
        // const containerGlobalPos = this.container.getGlobalPosition();
        const relativeCoord = new Vector2(
            this.x,
            this.y,
        );
        if (
            point.x > relativeCoord.x + this.width ||
            point.x < relativeCoord.x ||
            point.y > relativeCoord.y + this.height ||
            point.y < relativeCoord.y
        )
            return false;
        return true;
    }

    __translateParentCoordToLocalCoord(pos: Vector2) {
        return pos // pos.sub(new Vector2(this.x, this.y));
    }
}